'use client';

import {
  useUserContextSync,
  useUserContextActions,
} from '@/contexts/UserContext';
import { useSession } from 'next-auth/react';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  serverUserQueryOptions,
  serverOrgQueryOptions,
  serverOrgByNameQueryOptions,
} from '@/hooks/useServerIdentity';
import User from '@/types/userType';
import Organization from '@/types/organizationType';
import { Team } from '@/state/teamState';

type UserLogged = {
  user:
    | (User & {
        name?: string | null;
        email?: string | null;
        image?: string | null;
        provider?: string;
        accessToken?: string;
        username?: string;
        ghUserName?: string;
        glUserName?: string;
      })
    | null;
  organization: Organization | null;
  team: Team | null;
  status: 'authenticated' | 'loading' | 'unauthenticated';
  // True once org resolution has genuinely finished (see UserContext). Only
  // then does `organization: null` mean "this user has no workspace" rather
  // than "still loading".
  orgResolutionDone: boolean;
};

// `userQuery.data` is the plain backend row (never session-merged, per the
// guardrail in useServerIdentity.ts), so it is typed as plain `User`. All of
// `mergedUser`'s extra fields are optional, so a bare `User` value already
// satisfies this widened shape structurally -- this alias only exists so TS
// lets the merge below read them.
type SessionMergedUser = NonNullable<UserLogged['user']>;

export const useUserLogged = (): UserLogged => {
  const { data: session, status } = useSession();
  const { user: sessionUser } = session ?? {};

  // Sync with existing stores. Other direct `useUserContext()` consumers
  // (LoginPage, GitOrgNameSettingsCard, WorkspaceSetup, useCodebaseRepos)
  // still depend on this mirror; this hook no longer reads its output.
  useUserContextSync();
  const { fetchUser } = useUserContextActions();

  // Identity now lives in TanStack Query, seeded server-side by
  // `DashboardIdentity` (app/(dashboard)/layout.tsx) via HydrationBoundary,
  // so it is available on the very first render instead of only after a
  // client fetch. `enabled` is gated on session status settling rather than
  // on `sessionUser` alone: while `useSession()` is still 'loading',
  // `sessionUser` is momentarily undefined even for an authenticated
  // visitor, and firing the query then would let the fixed query key settle
  // on a bogus "no user" before the real session is known -- and, unlike an
  // effect, nothing would ever retry that (staleTime is 3 minutes and the
  // key never changes).
  const userQuery = useQuery({
    ...serverUserQueryOptions(sessionUser),
    enabled: status !== 'loading',
  });
  const user: SessionMergedUser | null = userQuery.data ?? null;

  const orgUUID = user?.OrganizationUUID;
  // undefined = client has not read localStorage yet (also the SSR value).
  // null = checked, no name. string = legacy name-only org.
  const [legacyOrgName, setLegacyOrgName] = useState<string | null | undefined>(
    undefined
  );

  useEffect(() => {
    if (orgUUID) {
      setLegacyOrgName(null);
      return;
    }
    setLegacyOrgName(window.localStorage.getItem('OrgName'));
  }, [orgUUID]);

  const orgQuery = useQuery({
    ...serverOrgQueryOptions(orgUUID),
    enabled: !!orgUUID,
  });
  const nameQueryEnabled = !orgUUID && !!legacyOrgName;
  const nameQuery = useQuery({
    ...serverOrgByNameQueryOptions(legacyOrgName ?? ''),
    enabled: nameQueryEnabled,
  });
  const organization = orgQuery.data ?? nameQuery.data ?? null;

  // `fetchTeam` (UserContext.tsx) is a permanent no-op; real team data flows
  // through useTeams()/useFetchTeams(), never through this hook.
  const team: Team | null = null;

  /**
   * Ports `GLOBAL_STATE.userInitialized && (orgLoaded || userHasNoOrg)`
   * (UserContext.tsx `fetchOrganization`) onto the two queries:
   *  - The user query must have settled successfully -- not pending, and
   *    not errored (an error must never read as "resolved", same as the
   *    original leaving `userInitialized` false on a fetch exception) --
   *    and resolved to a real row. A successfully-resolved "no such user"
   *    (`user` still null) also does not count, matching the original's
   *    `if (!user?.UserUUID) return;` guard, which left orgResolutionDone at
   *    its previous value instead of computing it.
   *  - Then either the org query actually produced a real org (`OrgUUID` or
   *    `OrgName` present), or the user genuinely has no `OrganizationUUID`.
   *    Checking the org query's `status` alone is not enough:
   *    `fetchOrganizationByUUID` swallows its own request failures and
   *    resolves to `null` on a down backend, so a bare `orgQuery.status ===
   *    'success'` can't tell "org fetched" apart from "org fetch silently
   *    failed" -- exactly the empty-org trap `useOrgUuid`'s docstring below
   *    describes, which the original avoided by checking the org data
   *    itself, not just whether an attempt completed.
   *  - A UUID-less user is not "genuinely no org" until the client has
   *    checked `localStorage.OrgName` and any by-name fetch has settled.
   *    Marking done on the server (no window) bounced legacy name-only rows
   *    to `/onboarding` before that fallback could run.
   */
  const orgLoaded = !!(organization?.OrgUUID || organization?.OrgName);
  const awaitingLegacyName = !orgUUID && legacyOrgName === undefined;
  const awaitingNameFetch = nameQueryEnabled && nameQuery.isPending;
  const orgResolutionDone =
    userQuery.status === 'success' &&
    !!user &&
    (orgLoaded ||
      (!user.OrganizationUUID && !awaitingLegacyName && !awaitingNameFetch));

  // Main ran `fetchUser(sessionUser)` on every authenticated dashboard mount.
  // That is the only producer of `setMismatch`. The server user query covers
  // the found-user path, so this only fires when that query settled on null.
  useEffect(() => {
    if (status !== 'authenticated' || !sessionUser) return;
    if (userQuery.status !== 'success' || user) return;
    void fetchUser(sessionUser);
  }, [status, sessionUser, userQuery.status, user, fetchUser]);

  // Merge session user data with stored user data, preserving stored data.
  // Memoized so `user` keeps its identity across renders: callers put it in
  // effect deps, and a fresh object every render loops any `[user]` effect
  // that sets state (it starved navigation away from /connectors).
  const mergedUser = useMemo(
    () =>
      user && sessionUser
        ? {
            ...user, // Keep all stored user data (UserUUID, OrganizationUUID, etc.)
            // Only add session-specific data that doesn't exist in stored user
            name: user.name || sessionUser.name,
            email: user.email || sessionUser.email,
            image: user.image || sessionUser.image,
            provider: user.provider || (sessionUser as any)?.provider,
            accessToken: user.accessToken || (sessionUser as any)?.accessToken,
            username: user.username || (sessionUser as any)?.username,
            ghUserName: user.ghUserName || (sessionUser as any)?.ghUserName,
            glUserName: user.glUserName || (sessionUser as any)?.glUserName,
          }
        : user,
    [user, sessionUser]
  );

  const hydratedFromServer =
    userQuery.status === 'success' && !!user?.UserUUID;
  const sessionReady = status === 'authenticated' && !!sessionUser?.email;

  if (sessionReady || (status === 'loading' && hydratedFromServer)) {
    return {
      user: mergedUser,
      team,
      organization,
      // DashboardLayoutClient gates on status === 'loading'. When the layout
      // already seeded identity, do not block the whole tree on next-auth
      // settling — that wedge was the blank-screen-on-leaderboard report.
      status: sessionReady ? status : 'authenticated',
      orgResolutionDone,
    };
  }

  return {
    status,
    user: null,
    organization: null,
    team: null,
    orgResolutionDone: false,
  };
};

/**
 * The caller's org UUID, stable across org resolution.
 *
 * Prefer this over `organization?.OrgUUID ?? user?.OrganizationUUID ?? ''`.
 * That expression looks equivalent and is not: `fetchOrganization` soft-fails
 * to `emptyOrganization`, whose `OrgUUID` is `''`, and `??` only falls through
 * on null/undefined — so an empty organization object yields `''` instead of
 * deferring to the user's own `OrganizationUUID`.
 *
 * Boot therefore walks a real uuid -> `''` -> the same real uuid. React Query
 * keys built from it change twice, and every query keyed on the org refetches
 * from scratch. Measured across 277 production Agent Insights loads, 22% ran
 * the full fan-out two or more times (up to five), each repeat re-issuing the
 * same ~250 KB, 20-graph-query request set.
 *
 * Falling back on any falsy value collapses that to one transition,
 * `''` -> the real uuid. Consumers already gate on `enabled: !!orgUuid`, so
 * nothing fires against the empty value. A genuine org switch still moves to a
 * different non-empty uuid and still refetches, which is correct.
 *
 * Deliberately NOT gated on `orgResolutionDone`. Two reasons:
 *
 * 1. It is not a tenant boundary. Every Agent Insights endpoint derives the
 *    org server-side from the authenticated principal
 *    (`org_uuid = getattr(request.user, "organizationUUID", None)`); none of
 *    them accepts a client-supplied org. This value picks a React Query cache
 *    key — it does not select whose data comes back.
 * 2. Requiring it would wedge the page. `orgResolutionDone` is
 *    `userInitialized && (orgLoaded || userHasNoOrg)`, and `fetchOrganization`
 *    soft-fails to `emptyOrganization` — so a user who HAS an
 *    `OrganizationUUID` but whose org payload came back empty leaves it false
 *    permanently. Returning `''` in that state means the route's spinner never
 *    resolves: the same empty-org trap this hook exists to close.
 *
 * Returns:
 *   The org UUID, or `''` while no org is known yet.
 */
export const useOrgUuid = (): string => {
  const { organization, user } = useUserLogged();
  return organization?.OrgUUID || user?.OrganizationUUID || '';
};

/**
 * Drop every cached query when the acting principal changes.
 *
 * Closes the one real hazard in reading an org uuid before resolution
 * finishes. The server never hands back another tenant's data — every Agent
 * Insights endpoint derives the org from the authenticated principal — but
 * React Query is keyed CLIENT-side, so a session that cached org A and then
 * switches to org B without a full reload can paint A's cached rows under A's
 * key while B resolves.
 *
 * Gating `useOrgUuid` on `orgResolutionDone` would also close it and is not
 * usable: that flag is `userInitialized && (orgLoaded || userHasNoOrg)`, and
 * `fetchOrganization` soft-fails to `emptyOrganization`, so a user who has an
 * `OrganizationUUID` whose org payload came back empty leaves it false
 * forever — an unresolving spinner. Clearing on change fixes the stale render
 * without making the page depend on a flag that can never arrive.
 *
 * The principal is `user + org`, not org alone: two accounts inside one org
 * share every org-keyed entry, so tracking only the org would let a second
 * account read the first's cached rows.
 *
 * Only a real change clears: the first transition out of "nothing known yet"
 * is a cold start with nothing to leak, and unchanged renders are a no-op.
 *
 * The last-seen principal is module-scoped rather than a ref because a ref
 * dies with the component. This hook mounts inside one route subtree, so a ref
 * would forget the previous principal on every navigation away and back —
 * exactly the window where a switch happens — and the clear would never fire.
 * Module scope lives as long as the QueryClient it guards.
 *
 * Scope: this only runs while a surface that mounts it is rendered. A switch
 * that happens entirely elsewhere and is fully resolved before arriving here
 * produces no transition to observe. Closing that needs the reset owned by a
 * component mounted across all dashboard routes, which is a broader change
 * than this belongs in.
 */
let lastSeenPrincipal: string | null = null;

export const useOrgScopedCacheReset = (): void => {
  const queryClient = useQueryClient();
  const { user, organization } = useUserLogged();
  const orgUuid = organization?.OrgUUID || user?.OrganizationUUID || '';
  const userUuid = user?.UserUUID || '';
  const principal = orgUuid && userUuid ? `${userUuid}|${orgUuid}` : '';

  useEffect(() => {
    if (!principal) return;
    if (lastSeenPrincipal && lastSeenPrincipal !== principal) {
      // Everything, not just org-keyed entries: on a principal change any
      // cached response was fetched as somebody else.
      queryClient.clear();
    }
    lastSeenPrincipal = principal;
  }, [principal, queryClient]);
};
